/**
 * 이상한 방 - 연결 배너 (끊김 · 다시 잇는 중 · 자리로 돌아왔다 · 서버의 거절, 순수 DOM)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.3절(시간은 서버가 쥔다 - 끊긴 동안에도 판은 흐른다)
 *            로드맵 M6(`connection.ts` - 끊김·재접속 배너. 페이즈 자막 자리 규약, **시계는 잡지 않는다**)
 *
 * ## 이 배너가 말하는 것은 넷이다
 *
 * 다시 잇는 중(끊김) · 자리로 돌아왔다(재접속 · 새로고침 복귀) · 연결이 끊겼다(서버가 끊었고 다시 잇지 않는다) · 서버의 거절 한 줄
 * (`net.error.<code>`). 값은 전부 전송의 상태(`net/wsTransport.onStatus`)와 서버 메시지에서 오고, 배너는 그것을 문장으로 옮길 뿐이다 -
 * "끊겼는가"를 배너가 스스로 재지 않는다(핑 시계가 여기 생기면 시계를 읽는 자리가 하나 늘어난다).
 *
 * ## 자리: 토스트(`.gh-notice`)와 같은 줄, 같은 문법
 *
 * HUD의 토스트가 서는 자리(상단 가운데, 페이즈 플레이트 아래)에 선다. 같은 자리인 이유는 둘이 동시에 뜰 일이 없고("자리로 돌아왔다"는
 * 곧 사라지고, "다시 잇는 중"인 동안 판정 토스트는 오지 않는다) 화면 중앙을 비우는 규칙 때문이다 - 연결 상태를 가운데 크게 띄우면
 * 그 시간이 곧 방을 못 보는 시간이다. 먹색 플레이트 · 기울기 · 하드 그림자까지 토스트와 같아 "HUD의 한 줄"로 읽힌다.
 *
 * ## 시계는 잡지 않는다
 *
 * 끊긴 동안에도 서버의 판은 흐른다. 배너가 세션의 시계를 멈추면 돌아온 뒤 첫 `clock`이 그만큼을 한 번에 당기고(`matchClock.ts`),
 * 사람은 시계가 튀는 것을 본다. 배너는 세션을 모른다 - 문장 하나를 띄우고 내리는 것이 전부다. "자리로 돌아왔다"만 타이머로 사라지고
 * 나머지는 다음 상태가 갈아 끼우거나 `hide`가 내린다. 정리 권한은 그 타이머 하나다(`ui/juice.ts`와 같은 규약).
 */

import { onLocaleChange, t } from '../i18n'
import type { ErrorCode } from '../net/protocol'

import { createElement, setAttr, setFlag, setText } from './dom'
import { publishSize } from './overlayLayout'

import './connection.css'

/* --- 상수 ---------------------------------------------------------------------- */

/** "자리로 돌아왔다"가 머무는 시간(ms). 읽고 잊을 만큼 - 판정 토스트(`gameHud.NOTICE_MS`)보다 조금 길다, 돌아온 순간은 눈이 화면을 다시 찾는 중이다. */
export const RESUMED_BANNER_MS = 2_600

/* --- 타입 ---------------------------------------------------------------------- */

/** 배너의 네 얼굴. `error`만 사유(`ErrorCode`)를 싣는다. */
export type ConnectionNotice =
  | Readonly<{ kind: 'reconnecting' }>
  | Readonly<{ kind: 'resumed' }>
  | Readonly<{ kind: 'closed' }>
  | Readonly<{ kind: 'error'; code: ErrorCode }>

export type ConnectionBannerHandle = Readonly<{
  root: HTMLElement
  /** 얼굴 하나를 띄운다. 앞 것은 갈아 끼운다. `resumed`만 스스로 사라진다. */
  show: (notice: ConnectionNotice) => void
  hide: () => void
  /** 지금 떠 있는 얼굴. 테스트·개발 훅용. */
  current: () => ConnectionNotice | null
  dispose: () => void
}>

export type ConnectionBannerOptions = Readonly<{ mount: HTMLElement }>

/* --- 문구 ---------------------------------------------------------------------- */

/** 얼굴 → 문장. 사전 키는 `net.*` 하나의 묶음이다 - 로비 폼의 오류 줄도 같은 키를 쓴다(`ui/lobby.ts`). */
export function connectionText(notice: ConnectionNotice): string {
  switch (notice.kind) {
    case 'reconnecting':
      return t('net.reconnecting')
    case 'resumed':
      return t('net.resumed')
    case 'closed':
      return t('net.closed')
    case 'error':
      return t(`net.error.${notice.code}`)
  }
}

/** 경고인가(빨강). 돌아온 것만 좋은 소식이다. */
function isWarn(notice: ConnectionNotice): boolean {
  return notice.kind !== 'resumed'
}

/* --- 본체 ---------------------------------------------------------------------- */

export function createConnectionBanner(options: ConnectionBannerOptions): ConnectionBannerHandle {
  const root = createElement('div', 'cn-root')
  root.setAttribute('role', 'status')
  root.setAttribute('aria-live', 'polite')
  const text = createElement('span', 'cn-text', '')
  root.append(text)
  options.mount.append(root)

  let current: ConnectionNotice | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  /*
   * 이 배너는 위 기둥(`.ol-stack`) **밖**에 산다 - 설정 패널(47)보다 위(48)여야 하는데 기둥은 22층이기 때문이다.
   * 대신 자기 높이를 내보내고 기둥이 그만큼 내려간다(`overlayLayout.ts` 머리말). 그래야 자리는 흐름과 똑같이 정해진다.
   *
   * 접히지 않고 **투명해지기만** 하는 상자라 사각형이 늘 남아 있다. 그래서 "지금 떠 있는가"를 문으로 넘긴다 -
   * 안 넘기면 아무 일도 없는 판에서 기둥이 배너 높이만큼 영원히 내려앉는다.
   */
  const size = publishSize(root, { height: '--cn-height', visible: () => current !== null })

  /*
   * 언어가 갈리면 떠 있는 줄도 다시 칠한다(로비·봉투·사물함과 같은 규약). "다시 잇는 중"·"연결이 끊겼다"는 스스로 사라지지 않고
   * 설정 패널은 판 도중에도 열리므로, 끊긴 채 언어를 바꾸는 조합이 실제로 도달 가능하다 - 그때 이 줄만 옛 언어로 남는다.
   */
  const stopLocaleWatch = onLocaleChange(() => {
    if (current !== null) setText(text, connectionText(current))
  })

  function clearTimer(): void {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
  }

  function hide(): void {
    clearTimer()
    current = null
    setFlag(root, 'is-visible', false)
    size.refresh()
  }

  return {
    root,

    show(notice: ConnectionNotice): void {
      clearTimer()
      current = notice
      setText(text, connectionText(notice))
      setAttr(root, 'data-tone', isWarn(notice) ? 'warn' : 'good')
      setAttr(root, 'data-kind', notice.kind)
      setFlag(root, 'is-visible', true)
      size.refresh()
      if (notice.kind === 'resumed') timer = setTimeout(hide, RESUMED_BANNER_MS)
    },

    hide,
    current: () => current,

    dispose(): void {
      clearTimer()
      stopLocaleWatch()
      size.dispose()
      root.remove()
    },
  }
}
